import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  Shield, Users, Key, Clock, Grid3X3, FileText, Search,
  ChevronRight, Check, X, Eye, Edit, Trash2, Settings,
  Download, CheckCircle, AlertTriangle, Loader2, Plus,
  RefreshCw, UserCog, Lock, Unlock
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";

// Permission level display
const PERMISSION_LEVELS = {
  full_platform_management: { label: "Full Platform Management", color: "bg-emerald-100 text-emerald-700" },
  department_management: { label: "Department Management", color: "bg-blue-100 text-blue-700" },
  operational_management: { label: "Operational Management", color: "bg-indigo-100 text-indigo-700" },
  standard_user: { label: "Standard User", color: "bg-gray-100 text-gray-700" },
  read_only: { label: "Read Only", color: "bg-amber-100 text-amber-700" },
  caregiver_access: { label: "Caregiver Access", color: "bg-purple-100 text-purple-700" },
};

// Portal labels
const PORTALS = {
  executive: "Executive",
  operations: "Operations",
  hr: "HR & Compliance",
  finance: "Finance",
  scheduler: "Scheduler",
  supervisor: "Supervisor",
  my_aligned: "My Leynix",
};

// Module labels
const MODULES = {
  clients: "Clients",
  employees: "Employees",
  scheduling: "Scheduling",
  payroll: "Payroll",
  billing: "Billing",
  compliance: "Compliance",
  hiring: "Hiring",
  reports: "Reports",
  agency_setup: "Agency Setup",
};

// Access level indicator
const AccessIndicator = ({ level }) => {
  const styles = {
    full: { icon: CheckCircle, color: "text-emerald-500", label: "Full" },
    partial: { icon: Edit, color: "text-blue-500", label: "Partial" },
    view: { icon: Eye, color: "text-amber-500", label: "View" },
    none: { icon: X, color: "text-gray-300", label: "None" },
  };
  const { icon: Icon, color, label } = styles[level] || styles.none;
  return (
    <div className="flex items-center justify-center" title={label}>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
  );
};

// User row in Access Matrix
const MatrixUserRow = ({ user, modules, onEdit }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50">
    <td className="py-3 px-4 sticky left-0 bg-white">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium">
          {user.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div>
          <p className="font-medium text-gray-900 text-sm">{user.name}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>
      </div>
    </td>
    <td className="py-3 px-4">
      <Badge className={PERMISSION_LEVELS[user.permission_level]?.color || "bg-gray-100"}>
        {PERMISSION_LEVELS[user.permission_level]?.label || user.permission_level}
      </Badge>
    </td>
    {modules.map((module) => (
      <td key={module} className="py-3 px-4 text-center">
        <AccessIndicator level={user.modules?.[module] || "none"} />
      </td>
    ))}
    <td className="py-3 px-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onEdit(user)}
        data-testid={`edit-user-${user.id}`}
      >
        <UserCog className="w-4 h-4" />
      </Button>
    </td>
  </tr>
);

// Template card
const TemplateCard = ({ template, onApply }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-all">
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="font-semibold text-gray-900">{template.name}</h3>
        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
      </div>
      {template.is_default && (
        <Badge variant="outline" className="text-xs">Default</Badge>
      )}
    </div>
    <div className="flex items-center gap-2 mt-4">
      <Badge className={PERMISSION_LEVELS[template.permission_level]?.color || "bg-gray-100"}>
        {PERMISSION_LEVELS[template.permission_level]?.label || template.permission_level}
      </Badge>
      {template.home_portal && (
        <Badge variant="outline" className="text-xs">
          Home: {PORTALS[template.home_portal] || template.home_portal}
        </Badge>
      )}
    </div>
  </div>
);

// Override card
const OverrideCard = ({ override, onRevoke }) => {
  const isExpired = new Date(override.expires_at) < new Date();
  
  return (
    <div className={`bg-white rounded-xl border p-4 ${isExpired ? "border-gray-200 opacity-60" : "border-amber-200 bg-amber-50/50"}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <h3 className="font-semibold text-gray-900">{override.user_name}</h3>
          </div>
          <p className="text-sm text-gray-500 mt-1">{override.user_email}</p>
        </div>
        {!isExpired && !override.revoked && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRevoke(override.id)}
            className="text-red-600 hover:text-red-700"
          >
            Revoke
          </Button>
        )}
      </div>
      
      <div className="mt-3 text-sm">
        <p className="text-gray-600"><strong>Reason:</strong> {override.reason}</p>
        <p className="text-gray-600 mt-1">
          <strong>Expires:</strong>{" "}
          {new Date(override.expires_at).toLocaleDateString()} at{" "}
          {new Date(override.expires_at).toLocaleTimeString()}
        </p>
        <p className="text-gray-500 text-xs mt-2">
          Granted by {override.granted_by_name} on {new Date(override.created_at).toLocaleDateString()}
        </p>
      </div>
      
      {override.revoked && (
        <Badge className="mt-2 bg-red-100 text-red-700">Revoked</Badge>
      )}
      {isExpired && !override.revoked && (
        <Badge className="mt-2 bg-gray-100 text-gray-600">Expired</Badge>
      )}
    </div>
  );
};

// User permissions editor modal
const UserPermissionsEditor = ({ user, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({
    permission_level: user?.permission_level || "standard_user",
    home_portal: user?.home_portal || "operations",
    portal_access: user?.portal_access || {},
    module_permissions: user?.module_permissions || {},
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/security/users/${user.id}/permissions`, permissions);
      toast.success("Permissions updated");
      onSave();
    } catch (err) {
      toast.error("Failed to update permissions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-[#06372B] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">{user?.name}</h2>
            <p className="text-white/70 text-sm">{user?.email}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] space-y-6">
          {/* Working As summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3">Working As</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Permission Level</p>
                <Badge className={PERMISSION_LEVELS[permissions.permission_level]?.color || "bg-gray-100"}>
                  {PERMISSION_LEVELS[permissions.permission_level]?.label}
                </Badge>
              </div>
              <div>
                <p className="text-gray-500">Home Portal</p>
                <p className="font-medium">{PORTALS[permissions.home_portal] || permissions.home_portal}</p>
              </div>
            </div>
          </div>

          {/* Permission Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permission Level</label>
            <Select
              value={permissions.permission_level}
              onValueChange={(v) => setPermissions({ ...permissions, permission_level: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PERMISSION_LEVELS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Home Portal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Home Portal</label>
            <Select
              value={permissions.home_portal}
              onValueChange={(v) => setPermissions({ ...permissions, home_portal: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PORTALS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Portal Access */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Portal Access</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PORTALS).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permissions.portal_access?.[key] || false}
                    onChange={(e) => setPermissions({
                      ...permissions,
                      portal_access: { ...permissions.portal_access, [key]: e.target.checked }
                    })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#06372B] hover:bg-[#0a4a3a]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Permissions
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Security Center component
export default function SecurityCenter() {
  const [activeTab, setActiveTab] = useState("matrix");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Data states
  const [matrix, setMatrix] = useState({ matrix: [], modules: [] });
  const [templates, setTemplates] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Editor state
  const [editingUser, setEditingUser] = useState(null);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [matrixRes, templatesRes, overridesRes, usersRes] = await Promise.all([
        api.get("/security/matrix"),
        api.get("/security/templates"),
        api.get("/security/overrides?active_only=false"),
        api.get("/security/users"),
      ]);
      setMatrix(matrixRes.data);
      setTemplates(templatesRes.data.templates || []);
      setOverrides(overridesRes.data.overrides || []);
      setUsers(usersRes.data.users || []);
    } catch (err) {
      console.error("Failed to load security data:", err);
      toast.error("Failed to load security data");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeOverride = async (overrideId) => {
    try {
      await api.post(`/security/overrides/${overrideId}/revoke`);
      toast.success("Override revoked");
      loadData();
    } catch (err) {
      toast.error("Failed to revoke override");
    }
  };

  const filteredMatrix = matrix.matrix?.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#06372B] flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security Center</h1>
            <p className="text-gray-500">Manage user permissions, roles, and access controls</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="matrix" className="flex items-center gap-2 rounded-lg">
            <Grid3X3 className="w-4 h-4" />
            Access Matrix
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2 rounded-lg">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2 rounded-lg">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="overrides" className="flex items-center gap-2 rounded-lg">
            <Clock className="w-4 h-4" />
            Overrides
          </TabsTrigger>
        </TabsList>

        {/* Access Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            <span className="font-medium">Legend:</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-500" /> Full</span>
            <span className="flex items-center gap-1"><Edit className="w-4 h-4 text-blue-500" /> Partial</span>
            <span className="flex items-center gap-1"><Eye className="w-4 h-4 text-amber-500" /> View</span>
            <span className="flex items-center gap-1"><X className="w-4 h-4 text-gray-300" /> None</span>
          </div>

          {/* Matrix Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 sticky left-0 bg-gray-50">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Level</th>
                  {matrix.modules?.map((module) => (
                    <th key={module} className="text-center py-3 px-4 font-medium text-gray-700 whitespace-nowrap">
                      {MODULES[module] || module}
                    </th>
                  ))}
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMatrix.map((user) => (
                  <MatrixUserRow
                    key={user.id}
                    user={user}
                    modules={matrix.modules || []}
                    onEdit={setEditingUser}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="grid gap-4">
            {users.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-lg font-medium">
                      {user.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={PERMISSION_LEVELS[user.permission_level]?.color || "bg-gray-100"}>
                          {PERMISSION_LEVELS[user.permission_level]?.label}
                        </Badge>
                        <Badge variant="outline">
                          Home: {PORTALS[user.home_portal] || user.home_portal}
                        </Badge>
                        {user.has_active_override && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Override Active
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingUser(user)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage
                  </Button>
                </div>

                {/* Portal Access */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Portal Access</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PORTALS).map(([key, label]) => (
                      <span
                        key={key}
                        className={`text-xs px-2 py-1 rounded-full ${
                          user.portal_access?.[key]
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {user.portal_access?.[key] ? <Check className="w-3 h-3 inline mr-1" /> : null}
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-gray-500">
              Permission templates are starting points. Customize individual users after applying.
            </p>
            <Button className="bg-[#06372B] hover:bg-[#0a4a3a]">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        </TabsContent>

        {/* Overrides Tab */}
        <TabsContent value="overrides" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-gray-500">
              Temporary permission overrides with automatic expiration.
            </p>
            <Button className="bg-[#06372B] hover:bg-[#0a4a3a]">
              <Plus className="w-4 h-4 mr-2" />
              Grant Override
            </Button>
          </div>

          {overrides.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No emergency overrides</p>
              <p className="text-sm">Grant temporary access when someone needs to cover</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {overrides.map((override) => (
                <OverrideCard
                  key={override.id}
                  override={override}
                  onRevoke={handleRevokeOverride}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* User Editor Modal */}
      {editingUser && (
        <UserPermissionsEditor
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={() => {
            setEditingUser(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
